import { Trans } from "@lingui/react/macro";
import styles from "./activityCard.module.scss";
import { isPast } from "date-fns";
import { CardHeading } from "./cardHeading";
import {
  categorySchema,
  type IMeasurementsType,
} from "@medoma/models/activities";
import {
  measurementsDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { Text } from "@medoma/components/Text/Text";
import clsx from "clsx";
import { useNavigate } from "react-router";
import { FilledButton } from "@medoma/components/Button/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  activityCategoryDictionary,
  finishActivityOccurrence,
} from "@/api/activities";
import { AssigneeList } from "./assigneeList";
import { generateQueryString } from "@/api/Helpers";
import { isAssigned, type IOccurrence } from "@/api/schedule";
import * as Sentry from "@sentry/react";
import { useLingui } from "@lingui/react/macro";
import {
  isErrorWithKnownErrorCode,
  knownErrorCodeSchema,
} from "@/utils/errorUtils";
import { humanFormat } from "@medoma/models/date-and-time";

type IActivityCard = {
  activityOccurrence: IOccurrence;
};

export const ActivityCard = ({ activityOccurrence }: IActivityCard) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: finishActivityOccurrenceMutation, isPending } = useMutation({
    mutationFn: ({
      activityId,
      occurrenceId,
    }: {
      activityId: string;
      occurrenceId: string;
    }) => {
      return finishActivityOccurrence(activityId, occurrenceId);
    },
    onError: (error) => {
      if (
        isErrorWithKnownErrorCode(error) &&
        error.data.code === knownErrorCodeSchema.enum.OccurrenceFinished
      ) {
        return queryClient.invalidateQueries({ queryKey: ["schedule"] });
      } else {
        const errorMessage = t`Det gick inte att markera aktiviteten som klar. Försök igen, eller skriv till oss i chatten så hjälper vi dig.`;
        Sentry.captureException(
          new Error(`An error alert was shown: ${errorMessage}`),
        );
        alert(errorMessage);
      }
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ["schedule"] });
    },
  });

  const {
    title: originalTitle,
    start,
    end,
    category,
    status,
    id: occurrenceId,
    activityId,
    timeOfDay,
  } = activityOccurrence;

  const callToAction =
    category === categorySchema.enum.PatientTask && isPast(start) ? (
      <div className={styles.cta}>
        <FilledButton
          size="large"
          disabled={isPending}
          onClick={() =>
            finishActivityOccurrenceMutation({ activityId, occurrenceId })
          }
        >
          <Trans>Markera som klar</Trans>
        </FilledButton>
      </div>
    ) : category === categorySchema.enum.PatientMeasurementTask &&
      isPast(start) ? (
      <div className={styles.cta}>
        <FilledButton
          size="large"
          onClick={() =>
            navigate(
              `/measurements${generateQueryString(
                mapMeasurementsToQueryParameters(
                  activityOccurrence.measurements,
                  occurrenceId,
                  activityId,
                ),
              )}`,
            )
          }
        >
          <Trans>Gå till mätning</Trans>
        </FilledButton>
      </div>
    ) : null;

  const startTime = start ? humanFormat(start, { timeStyle: "short" }) : null;
  const endTime = end ? humanFormat(end, { timeStyle: "short" }) : null;

  const activityOccurrenceWithAssignedEmployees = {
    ...activityOccurrence,
    assignees:
      category !== categorySchema.enum.PatientTask &&
      category !== categorySchema.enum.PatientMeasurementTask &&
      status !== "notReady"
        ? activityOccurrence.assignees.filter(isAssigned)
        : [],
  };
  const activityOccurrenceHasAssignedEmployees =
    activityOccurrenceWithAssignedEmployees.assignees.length > 0;

  const timeSpanText =
    timeOfDay === timeOfDaySchema.enum.Any
      ? t`Under dagen`
      : startTime && endTime
        ? t`Mellan ${startTime} och ${endTime}`
        : null;

  const contactText =
    timeOfDay === timeOfDaySchema.enum.Any
      ? t`Vi kontaktar dig under dagen`
      : startTime && endTime
        ? t`Vi kontaktar dig mellan ${startTime} och ${endTime}`
        : null;

  const pushNotificationText = t`Du får en notis när samtalet startar`;
  const videoCallText = `${contactText}. ${pushNotificationText}.`;

  const clinicVisitText =
    timeOfDay === timeOfDaySchema.enum.Any
      ? t`Du ska besöka oss under dagen`
      : startTime
        ? t`Du ska besöka oss klockan ${startTime}`
        : null;

  const title =
    category === categorySchema.enum.ClinicVisit
      ? t(activityCategoryDictionary[categorySchema.enum.ClinicVisit])
      : originalTitle;

  return (
    <article className={clsx(styles.activityCard)}>
      <CardHeading
        category={category}
        title={title}
        formattedTimespan={
          category === categorySchema.enum.VideoCall
            ? videoCallText
            : category === categorySchema.enum.ClinicVisit
              ? clinicVisitText
              : timeSpanText
        }
      />
      {category === categorySchema.enum.ClinicVisit ? (
        <Text element="span" weight="regular" size="small">
          {originalTitle}
        </Text>
      ) : null}
      {(category === categorySchema.enum.VideoCall ||
        category === categorySchema.enum.ClinicVisit) &&
      activityOccurrenceHasAssignedEmployees ? (
        <AssigneeList
          assignees={activityOccurrenceWithAssignedEmployees.assignees}
        />
      ) : category === categorySchema.enum.PatientMeasurementTask ? (
        <ul>
          {activityOccurrence.measurements.map((measurement) => (
            <li key={measurement}>
              <Text element="span" weight="regular" size="small">
                {t(measurementsDictionary[measurement])}
              </Text>
            </li>
          ))}
        </ul>
      ) : null}
      {callToAction}
    </article>
  );
};

const mapMeasurementsToQueryParameters = (
  measurements: IMeasurementsType[],
  occurrenceId: string,
  activityId: string,
) => {
  const queryParameters = {
    "blood-pressure-sys": false,
    "blood-pressure-dia": false,
    "blood-glucose": false,
    weight: false,
    temperature: false,
    saturation: false,
    pulse: false,
    activityId,
    occurrenceId,
    // Tell the backend to put `destination=schedule` in the reply URL
    // so that the native app can redirect back to the schedule
    destination: "schedule",
  };

  measurements.forEach((measurement) => {
    switch (measurement) {
      case "bloodPressure":
        queryParameters["blood-pressure-sys"] = true;
        queryParameters["blood-pressure-dia"] = true;
        break;
      case "bloodGlucose":
        queryParameters["blood-glucose"] = true;
        break;
      case "weight":
        queryParameters.weight = true;
        break;
      case "temperature":
        queryParameters.temperature = true;
        break;
      case "saturation":
        queryParameters.saturation = true;
        break;
      case "pulse":
        queryParameters.pulse = true;
        break;
      default:
        throw measurement satisfies never;
    }
  });

  // only return if true
  return Object.fromEntries(
    Object.entries(queryParameters).filter(([, value]) => value),
  );
};
